// Salon-built email templates ("My Templates").
//
// BACKED BY THE API since June 2026 (v2/marketing/templates) — localStorage
// is the cache; call hydrateUserTemplates() on mount and mutations write
// through. Templates are creator-scoped server-side (an SE's templates are
// theirs, not the salon's). Capped to the 30 most recent in the cache.

import { marketingBackend, isServerId } from '../../../services/marketingBackend';

const storageKey = (salonId) => `beu-email-templates-${salonId || 'default'}`;

const persist = (salonId, list) => {
  try { localStorage.setItem(storageKey(salonId), JSON.stringify(list)); } catch (e) { /* ignore */ }
  return list;
};

const fromServer = (t) => ({
  id: t._id || t.id,
  name: t.name,
  subject: t.subject || '',
  blocks: t.blocks || [],
  updatedAt: t.updatedAt,
});

/** Pull the creator's templates from the backend into the cache. */
export const hydrateUserTemplates = async (salonId) => {
  try {
    const server = await marketingBackend.templatesList();
    return persist(salonId, (server.templates || []).map(fromServer));
  } catch (e) {
    console.error('templates hydrate failed (cache kept):', e.message);
    return loadUserTemplates(salonId);
  }
};

// Which trade a saved template is for ('hair', 'barber', 'beauty' or 'all'),
// so the library shows it in the right section. Kept on the footer block,
// like the brand, so the templates API stores it without a schema change.
const TEMPLATE_TRADES = ['hair', 'barber', 'beauty', 'all'];
export const templateTrade = (tpl, fallback = 'all') => {
  const footer = (tpl && Array.isArray(tpl.blocks) ? tpl.blocks : []).find(b => b && b.type === 'footer');
  const trade = footer && footer.content && footer.content.templateTrade;
  return TEMPLATE_TRADES.includes(trade) ? trade : fallback;
};
export const withTemplateTrade = (blocks, trade) => (Array.isArray(blocks) ? blocks : []).map(b => (
  b && b.type === 'footer' ? { ...b, content: { ...(b.content || {}), templateTrade: trade } } : b
));

export const loadUserTemplates = (salonId) => {
  try {
    const raw = localStorage.getItem(storageKey(salonId));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
};

export const saveUserTemplate = (salonId, { id, name, subject, blocks }) => {
  const list = loadUserTemplates(salonId);
  const tpl = {
    id: id || `utpl-${Date.now()}`,
    name: name || 'Untitled template',
    subject: subject || '',
    blocks: blocks || [],
    updatedAt: new Date().toISOString(),
  };
  const next = [tpl, ...list.filter(t => t.id !== tpl.id)].slice(0, 30);
  persist(salonId, next);
  marketingBackend.templateSave({
    id: isServerId(tpl.id) ? tpl.id : undefined,
    name: tpl.name, subject: tpl.subject, blocks: tpl.blocks,
  }).then((server) => {
    const fresh = fromServer(server.template);
    persist(salonId, [fresh, ...loadUserTemplates(salonId).filter(t => t.id !== tpl.id && t.id !== fresh.id)].slice(0, 30));
  }).catch((e) => console.error('template save failed:', e.message));
  return tpl;
};

export const deleteUserTemplate = (salonId, id) => {
  const next = loadUserTemplates(salonId).filter(t => t.id !== id);
  persist(salonId, next);
  if (isServerId(id)) {
    marketingBackend.templateDelete(id).catch((e) => console.error('template delete failed:', e.message));
  }
  return next;
};
